import { html, nothing } from "lit-html";
import { DropdownDialog } from "../DropdownDialog";
import { classMap } from "lit-html/directives/class-map.js";
import { Cache } from "@escapio/portal/state/Cache.js";
import { translate } from "@escapio/shared/l10n/translate";
import { SIZE_MOBILE, getVersion } from "@escapio/portal/dom/screen";
import "../Scrollband";
import "./Calendar";

const STORAGE_KEY_CALENDAR = "calendar-shown";
const TTL = 24 * 60 * 60 * 1000;
const cache = new Cache(STORAGE_KEY_CALENDAR, TTL);
cache.gc();

export const hasBeenShown = () => cache.get(STORAGE_KEY_CALENDAR);

export class CalendarDialog extends DropdownDialog {
	constructor(anchorElement) {
		super(anchorElement);
		this.node.classList.add("calendar-dialog");
	}

	getTemplateContent() {
		return html`
			<esc-scrollband
				scroller="esc-calendar"
				visible-elements="tablet:1,desktop:2"
			>
				<esc-calendar
					.selectionStart=${this.arrival}
					.selectionEnd=${this.departure}
					@startDateSelected=${this.onStartDateSelected}
					@endDateSelected=${this.onEndDateSelected}
					@invalidDateRangeSelected=${this.onInvalidSelection}
				></esc-calendar>
				<!-- ESC-9017,ESC-9659: For some reason chrome does not like button elements within the scroller -->
				<div
					role="button"
					class="secondary-button calendar-dialog__button calendar-dialog__button--left fa fa-angle-left"
					@click=${this.onScrollButtonClick}
					data-dir="left"
				></div>
				<div
					role="button"
					class="secondary-button calendar-dialog__button calendar-dialog__button--right fa fa-angle-right"
					@click=${this.onScrollButtonClick}
					data-dir="right"
				></div>
			</esc-scrollband>
		`;
	}

	getTemplateFooter() {
		return html`<div
			class="dialog__bottom-bar ${classMap({
				"dialog__bottom-bar--show":
					this.showConfirmButton || this.showResetButton,
			})}"
		>
			${this.showResetButton
				? html`<button
						class="inverted-action-button dialog__bottom-bar__button"
						@click=${this.resetDates}
					>
						${translate("calendar_button_continue_without_dates")}
					</button>`
				: nothing}
			${this.showConfirmButton
				? html`
						<button
							class="action-button dialog__bottom-bar__button"
							?disabled=${this.invalid || !this.showConfirmButton}
							@click=${this.onConfirmButtonClick}
						>
							${this.invalid
								? translate("calendar_error_message_date_range")
								: translate("basic_confirm")}
						</button>
					`
				: nothing}
		</div>`;
	}

	onScrollButtonClick({ target }) {
		target.dispatchEvent(
			new CustomEvent("ScrollTo", {
				detail: target.getAttribute("data-dir"),
				bubbles: true,
			}),
		);
	}

	onConfirmButtonClick() {
		this.node.dispatchEvent(
			new CustomEvent("confirm", {
				detail: { arrival: this.arrival, departure: this.departure },
			}),
		);
		this.hide();
	}

	resetDates() {
		this.node.dispatchEvent(
			new CustomEvent("confirm", {
				detail: { arrival: null, departure: null },
			}),
		);
		this.hide();
	}

	onStartDateSelected({ detail: { date } }) {
		this.showConfirmButton = false;
		this.showResetButton = false;
		this.arrival = date;
		this.departure = null;
		this.render();
		this.notify();
	}

	onEndDateSelected({ detail: { date } }) {
		this.invalid = false;
		this.showConfirmButton = true;
		this.departure = date;
		this.render();
		this.notify();
	}

	onInvalidSelection() {
		this.invalid = true;
		this.showConfirmButton = true;
		this.render();
	}

	show(arrival, departure, zIndex = null) {
		this.invalid = false;
		this.showConfirmButton = false;
		this.showResetButton = true;
		this.arrival = arrival;
		this.departure = departure;
		super.show(zIndex);
		cache.set(STORAGE_KEY_CALENDAR, true);
	}

	onShow() {
		const calendar = this.node.querySelector("esc-calendar");
		if (getVersion() === SIZE_MOBILE) {
			calendar.scrollSelectionIntoView();
		} else {
			const monthNode = calendar.getMonthNodeByDate(this.arrival);
			const scrollband = this.node.querySelector("esc-scrollband");
			scrollband.scrollNodeIntoView(monthNode);
		}
	}

	notify() {
		this.node.dispatchEvent(
			new CustomEvent("change", {
				detail: { arrival: this.arrival, departure: this.departure },
			}),
		);
	}
}
